import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import type { FramesManager } from 'shared-libs/components/view/frames-manager'
import type { AddressValue, UI2AddressFieldProps } from 'shared-libs/components/ui2/fields/ui2-address-field/ui2-address-field-props'
import { FieldShellContext } from 'shared-libs/components/ui2/fields/field-ui/field-shell/field-shell-context'

import { Input } from 'browser/components/atomic-elements/atoms/input/input'
import { UI2PlatformContext as FormFieldPlatformContext } from 'browser/components/ui2/forms/ui2-form/ui2-platform-context'
import { UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'
import COUNTRIES from 'shared-libs/helpers/countries'
import { Formatter } from 'shared-libs/helpers/formatter'
import { getAddressFromGooglePlace } from 'shared-libs/helpers/google-places'

import { UI2SelectField } from '../ui2-select-field/ui2-select-field'
import { IUI2FieldShellProps, UI2FieldShell } from '../field-ui/field-shell/field-shell'

import './_ui2-address-field.scss'

declare let window: any

interface IUI2AddressWebExtras {
  onChange?: (value: any) => void
  className?: string
  style?: React.CSSProperties
  frames?: FramesManager
  translationTable?: any
  errors?: any[]
  isRequired?: boolean
  placeholder?: string
}

/**
 * @uiComponent
 */
export interface IUI2AddressFieldProps extends UI2AddressFieldProps, IUI2AddressWebExtras {}

const SUB_FIELDS: Array<keyof AddressValue> = [
  'streetAddress',
  'extendedAddress',
  'locality',
  'region',
  'postalCode',
  'countryName',
]

const REQUIRED_SUB_FIELDS: Array<keyof AddressValue> = [
  'streetAddress',
  'locality',
  'region',
  'postalCode',
  'countryName',
]

function isSubFieldEmpty(value: AddressValue | undefined, key: keyof AddressValue): boolean {
  return _.isEmpty(_.get(value, key))
}

interface ITextRowProps {
  inputRef?: (ref: any) => void
  className?: string
  placeholder: string
  value: string
  disabled?: boolean
  hasError?: boolean
  autoComplete?: string
  onChange: (newValue: string) => void
}

const TextRow = React.memo<ITextRowProps>(function TextRow({
  inputRef,
  className,
  placeholder,
  value,
  disabled,
  hasError,
  autoComplete,
  onChange,
}) {
  return (
    <div className={classNames('ui2-addressField-row', className)}>
      <Input
        extraRef={inputRef}
        className='ui2-addressField-inputAtom'
        inputClassName={classNames('ui2-addressField-textInput', {
          'ui2-addressField-textInput--hasError': hasError,
        })}
        errorText={hasError ? ' ' : undefined}
        placeholder={placeholder}
        value={value}
        isDisabled={disabled}
        autoComplete={autoComplete}
        onChange={onChange}
      />
    </div>
  )
})

function isEmptyAddress(value: AddressValue | null | undefined): boolean {
  if (_.isNil(value)) return true
  return _.every(SUB_FIELDS, (k) => _.isEmpty(_.get(value, k)))
}

function formatAddress(value: AddressValue | undefined): string {
  if (isEmptyAddress(value)) return ''
  const v = value!
  const street = _.compact([v.streetAddress, v.extendedAddress]).join(' ')
  const cityLine = _.compact([
    v.locality && `${v.locality}${v.region || v.postalCode ? ',' : ''}`,
    v.region,
    v.postalCode,
  ]).join(' ')
  return _.compact([street, cityLine, v.countryName]).join(' · ')
}

export class UI2AddressField extends React.Component<IUI2AddressFieldProps> {
  public static isValueEmptyForRequired(value: AddressValue | undefined): boolean {
    if (_.isNil(value)) return true
    return _.some(REQUIRED_SUB_FIELDS, (k) => isSubFieldEmpty(value, k))
  }

  private streetInputEl: HTMLInputElement | null = null
  private setStreetInputRef = (el: HTMLInputElement | null): void => {
    this.streetInputEl = el
  }
  private autocomplete: any = null

  private handleStreetChange = (v: string) => this.handleSubFieldChange('streetAddress', v)
  private handleExtendedChange = (v: string) => this.handleSubFieldChange('extendedAddress', v)
  private handleLocalityChange = (v: string) => this.handleSubFieldChange('locality', v)
  private handleRegionChange = (v: any) => this.handleSubFieldChange('region', v)
  private handlePostalChange = (v: string) => this.handleSubFieldChange('postalCode', v)
  private handleCountryChange = (v: any) => this.handleSubFieldChange('countryName', v)

  public componentDidMount() {
    this.attachAutocomplete()
  }

  public componentDidUpdate() {
    if (!this.autocomplete) this.attachAutocomplete()
  }

  public componentWillUnmount() {
    if (this.autocomplete && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(this.autocomplete)
    }
    this.autocomplete = null
  }

  private attachAutocomplete() {
    if (this.autocomplete) return
    const el = this.streetInputEl
    if (!el) return
    const Autocomplete = window.google?.maps?.places?.Autocomplete
    if (!Autocomplete) return
    this.autocomplete = new Autocomplete(el, {
      componentRestrictions: { country: ['us', 'ca'] },
      types: ['address'],
    })
    this.autocomplete.addListener('place_changed', this.handlePlaceChanged)
  }

  private handlePlaceChanged = () => {
    if (!this.autocomplete) return
    const place = this.autocomplete.getPlace()
    if (!place || _.isEmpty(place.address_components)) return
    const parsed = getAddressFromGooglePlace(place)
    if (!parsed) return
    this.props.onChange?.(parsed)
  }

  public render() {
    const { className } = this.props
    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-addressField', className)}
        renderReadOnly={this.renderReadOnly}
        isValueEmpty={isEmptyAddress as any}
      >
        {this.renderEditable()}
      </UI2FieldShell>
    )
  }

  private renderEditable(): React.ReactNode {
    const { value, isDisabled, errors, placeholder, isRequired, frames, translationTable } = this.props
    const address: AddressValue = (value && value.countryName)
      ? value
      : { ...(value || {}), countryName: 'United States' }
    const hasError = !_.isEmpty(errors)
    const showSubFieldErrors = hasError && !!isRequired
    const subFieldHasError = (k: keyof AddressValue): boolean =>
      showSubFieldErrors &&
      REQUIRED_SUB_FIELDS.includes(k) &&
      isSubFieldEmpty(address, k)

    const selectedCountry =
      _.find(COUNTRIES, (c) => c.name === address.countryName) ||
      _.find(COUNTRIES, (c) => c.code === address.countryName) ||
      _.find(COUNTRIES, (c) => c.name === 'United States')
    const regions = _.get(selectedCountry, 'regions', []) as Array<{ code: string; name: string }>
    const regionLabel = _.get(selectedCountry, 'regionLabel', 'State')
    const postalCodeLabel = _.get(selectedCountry, 'postalCodeLabel', 'ZIP')

    const regionOptions = regions.map((r) => ({ value: r.code, label: r.name }))
    const countryOptions = COUNTRIES.map((c: any) => ({ value: c.name, label: c.name }))

    return (
      <FieldShellContext.Consumer>
        {(shellCtx) => (
          <div
            className={classNames('ui2-addressField-inputWrapper', {
              'ui2-addressField-inputWrapper--isDisabled': isDisabled,
              'ui2-addressField-inputWrapper--hasError': hasError,
            })}
          >
            <div className='ui2-addressField-streetRow'>
              <div className='ui2-addressField-streetRowInput'>
                <TextRow
                  inputRef={this.setStreetInputRef}
                  placeholder={placeholder || 'Street Address'}
                  value={address.streetAddress || ''}
                  disabled={isDisabled}
                  hasError={subFieldHasError('streetAddress')}
                  autoComplete='off'
                  onChange={this.handleStreetChange}
                />
              </div>
              {shellCtx.prefilledCancelNode && (
                <div className='ui2-addressField-cancelSlot'>
                  {shellCtx.prefilledCancelNode}
                </div>
              )}
            </div>
            <TextRow
              placeholder='Apt./Suite'
              value={address.extendedAddress || ''}
              disabled={isDisabled}
              onChange={this.handleExtendedChange}
            />
            <TextRow
              placeholder='City'
              value={address.locality || ''}
              disabled={isDisabled}
              hasError={subFieldHasError('locality')}
              onChange={this.handleLocalityChange}
            />
            <div className='ui2-addressField-row'>
              <div className='ui2-addressField-cell ui2-addressField-cell--region'>
                <UI2SelectField
                  label=''
                  placeholder={regionLabel}
                  isClearable={false}
                  isSearchable
                  isDisabled={isDisabled || _.isEmpty(regions)}
                  options={regionOptions}
                  value={address.region}
                  onChange={this.handleRegionChange}
                  translationTable={translationTable}
                  errors={subFieldHasError('region') ? [' '] : undefined}
                />
              </div>
              <div className='ui2-addressField-cell ui2-addressField-cell--postal'>
                <Input
                  className='ui2-addressField-inputAtom'
                  inputClassName={classNames('ui2-addressField-textInput', {
                    'ui2-addressField-textInput--hasError': subFieldHasError('postalCode'),
                  })}
                  errorText={subFieldHasError('postalCode') ? ' ' : undefined}
                  inputMode='numeric'
                  placeholder={postalCodeLabel}
                  value={address.postalCode || ''}
                  isDisabled={isDisabled}
                  onChange={this.handlePostalChange}
                />
              </div>
            </div>
            <div className='ui2-addressField-row'>
              <div className='ui2-addressField-cell'>
                <UI2SelectField
                  label=''
                  placeholder='Country'
                  isClearable={false}
                  isSearchable
                  isDisabled={isDisabled}
                  options={countryOptions}
                  value={address.countryName}
                  onChange={this.handleCountryChange}
                  translationTable={translationTable}
                  errors={subFieldHasError('countryName') ? [' '] : undefined}
                />
              </div>
            </div>
          </div>
        )}
      </FieldShellContext.Consumer>
    )
  }

  private renderReadOnly = (value: AddressValue | undefined, { editNode }: { editNode?: React.ReactNode } = {}): React.ReactNode => {
    const { displayMode, showGPSAsDegrees } = this.props
    const geolocation = _.get(value, 'geolocation')
    const hasGeolocation = !!(geolocation && geolocation.latitude && geolocation.longitude)

    const text = displayMode === 'coordinates' && hasGeolocation
      ? Formatter.formatGeolocation(geolocation, showGPSAsDegrees)
      : formatAddress(value)

    if (!text) return (
      <>
        <div className='ui2-fieldShell-readOnly-content'><UI2NoValueAvailable /></div>
        {editNode}
      </>
    )

    const mapUrl = hasGeolocation
      ? Formatter.formatGeolocationAsGoogleMapUrl(geolocation)
      : null

    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>
          <FormFieldPlatformContext.Consumer>
            {(platform) => {
              const isMobileWeb = platform === 'mobileWeb'
              if (isMobileWeb && mapUrl) {
                return (
                  <div className='ui2-addressField-readOnly'>
                    <a
                      className='ui2-addressField-readOnlyCard'
                      href={mapUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <div className='ui2-addressField-readOnlyCardHeader'>
                        <Icon icon={IconNames.MAP_MARKER} className='ui2-addressField-readOnlyCardIcon' />
                        <span className='ui2-addressField-readOnlyCardText'>{text}</span>
                        <Icon icon={IconNames.CHEVRON_RIGHT} className='ui2-addressField-readOnlyCardChevron' />
                      </div>
                    </a>
                  </div>
                )
              }
              return (
                <div className='ui2-addressField-readOnly'>
                  <div className='ui2-addressField-readOnlyMain'>
                    {mapUrl ? (
                      <a
                        className='ui2-addressField-readOnlyLink'
                        href={mapUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {text}
                      </a>
                    ) : (
                      <span>{text}</span>
                    )}
                  </div>
                </div>
              )
            }}
          </FormFieldPlatformContext.Consumer>
        </div>
        {editNode}
      </>
    )
  }

  private handleSubFieldChange = (key: keyof AddressValue, newValue: any) => {
    const { value, onChange } = this.props
    const base: AddressValue = (value && value.countryName)
      ? value
      : { ...(value || {}), countryName: 'United States' }
    onChange?.({ ...base, [key]: newValue })
  }
}
